// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/lifecycle/LifecycleWorkbench.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Section,
  Skeleton,
  StatusChip,
  TextArea,
  TextInput,
} from "../../../design-system/components";

const TERMINAL_EXPORT = new Set(["completed", "waived"]);
const TERMINAL_REQUEST = new Set(["completed", "cancelled", "canceled"]);

const ACTION_CONFIG = {
  request: {
    eyebrow: "Governed lifecycle request",
    title: "Request tenant offboarding",
    description:
      "This creates a governed offboarding request and starts the export window. It does not delete tenant data or execute the system-side purge workflow.",
    confirmLabel: "Request offboarding",
    confirmVariant: "primary",
  },
  cancel: {
    eyebrow: "Governed lifecycle request",
    title: "Cancel offboarding request",
    description:
      "Cancel this request while it remains eligible for cancellation. No destructive system action is triggered by this control.",
    confirmLabel: "Cancel request",
    confirmVariant: "secondary",
  },
  export_completed: {
    eyebrow: "Export disposition",
    title: "Mark export completed",
    description:
      "Record that the customer-data export requirement has been completed. A completion reference is required for the lifecycle record.",
    confirmLabel: "Mark completed",
    confirmVariant: "primary",
  },
  export_waived: {
    eyebrow: "Export disposition",
    title: "Waive customer-data export",
    description:
      "Record an explicit export waiver. A reason is required and remains part of the governed lifecycle record.",
    confirmLabel: "Waive export",
    confirmVariant: "secondary",
  },
  approve: {
    eyebrow: "Governed approval",
    title: "Approve offboarding",
    description:
      "Approval authorizes the governed system workflow when all holds, timing requirements, and execution gates permit. Approval does not delete the tenant immediately.",
    confirmLabel: "Approve offboarding",
    confirmVariant: "primary",
  },
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalized(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, "_");
}

function humanize(value, fallback = "Not available") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function shortId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Not recorded";
  return raw.length > 20 ? `${raw.slice(0, 8)}…${raw.slice(-8)}` : raw;
}

function statusTone(value) {
  const state = normalized(value);
  if (["completed", "succeeded", "verified", "eligible", "approved"].includes(state)) {
    return "success";
  }
  if (["blocked", "hold", "failed", "error"].includes(state)) return "danger";
  if (["requested", "pending", "waiting", "in_progress", "running"].includes(state)) {
    return "warning";
  }
  if (["cancelled", "canceled", "waived"].includes(state)) return "neutral";
  return "informational";
}

function getExportDisposition(request) {
  const value = request?.export_disposition;
  if (typeof value === "string") return { status: value };
  return asObject(value);
}

function getApproval(request) {
  return asObject(request?.approval || request?.approval_state);
}

function getOrchestration(request) {
  return asObject(request?.orchestration || request?.orchestration_state);
}

function getExecution(request) {
  return asObject(request?.execution || request?.execution_state);
}

function getFinalization(request) {
  return asObject(request?.finalization || request?.finalization_state);
}

function getCompletion(request) {
  return asObject(
    request?.completion ||
      request?.completion_evidence ||
      request?.completion_run ||
      request?.completion_ledger
  );
}

function getBlockers(request) {
  const direct = request?.blockers || request?.visible_blockers;
  if (Array.isArray(direct)) return direct;
  const lifecycle = asObject(request?.lifecycle_holds || request?.holds);
  if (Array.isArray(lifecycle.items)) return lifecycle.items;
  return [];
}

function blockerLabel(blocker) {
  if (typeof blocker === "string") return humanize(blocker);
  const item = asObject(blocker);
  return (
    item.label ||
    item.message ||
    item.reason ||
    item.code ||
    item.type ||
    "Lifecycle blocker"
  );
}

function getRequestActions(request) {
  return asObject(request?.actions);
}

function activeRequestFromView(view) {
  if (view?.active_request && typeof view.active_request === "object") return view.active_request;
  const items = asArray(view?.items);
  return (
    items.find((item) => item?.is_active === true) ||
    items.find((item) => !TERMINAL_REQUEST.has(normalized(item?.status))) ||
    null
  );
}

function completionVerified(request) {
  const completion = getCompletion(request);
  const finalization = getFinalization(request);
  if (completion.verified === true || completion.completion_verified === true) return true;
  if (finalization.completion_verified === true) return true;
  return normalized(request?.status) === "completed";
}

function purgeEligibility(request) {
  const eligibility = asObject(request?.purge_eligibility);
  if (typeof request?.purge_eligible === "boolean") return request.purge_eligible;
  if (typeof eligibility.eligible === "boolean") return eligibility.eligible;
  if (typeof eligibility.is_eligible === "boolean") return eligibility.is_eligible;
  return null;
}

function processSteps(request) {
  if (!request) return [];

  const exportDisposition = getExportDisposition(request);
  const approval = getApproval(request);
  const orchestration = getOrchestration(request);
  const finalization = getFinalization(request);
  const exportStatus = normalized(exportDisposition.status);
  const requestStatus = normalized(request.status);
  const approvalComplete =
    Boolean(request.approved_at) ||
    approval.approved === true ||
    normalized(approval.status) === "approved";
  const eligible = purgeEligibility(request);
  const orchestrationPhase = normalized(
    orchestration.phase || orchestration.status || request.orchestration_phase
  );
  const complete = completionVerified(request);

  return [
    {
      key: "request",
      label: "Request",
      detail: `Created ${formatDateTime(request.requested_at || request.created_at)}`,
      state: "complete",
    },
    {
      key: "export",
      label: "Export disposition",
      detail: exportStatus ? humanize(exportStatus) : "Disposition pending",
      state: TERMINAL_EXPORT.has(exportStatus) ? "complete" : "active",
    },
    {
      key: "approval",
      label: "Approval",
      detail: approvalComplete
        ? `Approved ${formatDateTime(request.approved_at || approval.approved_at)}`
        : "Governed approval pending",
      state: approvalComplete ? "complete" : TERMINAL_EXPORT.has(exportStatus) ? "active" : "pending",
    },
    {
      key: "eligibility",
      label: "Export window / purge eligibility",
      detail:
        eligible === true
          ? "Purge eligibility reached"
          : eligible === false
            ? `Eligible ${formatDateTime(request.purge_eligible_at)}`
            : `Eligibility ${formatDateTime(request.purge_eligible_at)}`,
      state: eligible === true ? "complete" : approvalComplete ? "active" : "pending",
    },
    {
      key: "orchestration",
      label: "System orchestration",
      detail: orchestrationPhase
        ? humanize(orchestrationPhase)
        : requestStatus === "in_progress"
          ? "In progress"
          : "Awaiting governed system workflow",
      state:
        complete || ["completed", "finalized"].includes(orchestrationPhase)
          ? "complete"
          : requestStatus === "in_progress" || ["running", "in_progress"].includes(orchestrationPhase)
            ? "active"
            : "pending",
    },
    {
      key: "completion",
      label: "Completion verified",
      detail: complete
        ? `Verified ${formatDateTime(request.completed_at || finalization.completed_at)}`
        : "Protected completion evidence pending",
      state: complete ? "complete" : "pending",
    },
  ];
}

function LifecycleMetric({ label, value, detail }) {
  return (
    <Card className="enterprise-admin-lifecycle-metric" density="compact">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </Card>
  );
}

function LifecycleProcess({ request }) {
  const steps = processSteps(request);
  return (
    <div className="enterprise-admin-lifecycle-process" aria-label="Governed offboarding progression">
      {steps.map((step, index) => (
        <React.Fragment key={step.key}>
          <div className={`enterprise-admin-lifecycle-step is-${step.state}`}>
            <span className="enterprise-admin-lifecycle-step-marker" aria-hidden="true">
              {step.state === "complete" ? "✓" : index + 1}
            </span>
            <div>
              <strong>{step.label}</strong>
              <small>{step.detail}</small>
            </div>
          </div>
          {index < steps.length - 1 ? (
            <span className="enterprise-admin-lifecycle-step-connector" aria-hidden="true" />
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function LifecycleHistory({ items }) {
  if (!items.length) return null;

  return (
    <div className="enterprise-admin-lifecycle-history">
      {items.map((item) => {
        const blockers = getBlockers(item);
        return (
          <Card key={item.id || `${item.status}-${item.created_at}`} density="compact">
            <div className="enterprise-admin-lifecycle-history-head">
              <div>
                <strong>{humanize(item.status, "Lifecycle request")}</strong>
                <small>{formatDateTime(item.requested_at || item.created_at)}</small>
              </div>
              <StatusChip tone={statusTone(item.status)}>{humanize(item.status)}</StatusChip>
            </div>
            <dl className="enterprise-admin-lifecycle-facts compact">
              <div><dt>Request</dt><dd title={item.id || ""}>{shortId(item.id)}</dd></div>
              <div><dt>Export</dt><dd>{humanize(getExportDisposition(item).status, "Pending")}</dd></div>
              <div><dt>Approval</dt><dd>{item.approved_at ? "Approved" : "Not approved"}</dd></div>
              <div><dt>Blockers</dt><dd>{blockers.length}</dd></div>
            </dl>
          </Card>
        );
      })}
    </div>
  );
}

function LifecycleCompletionEvidence({ request }) {
  if (!request || !completionVerified(request)) return null;

  const completion = getCompletion(request);
  const execution = getExecution(request);
  const finalization = getFinalization(request);
  const run = asObject(completion.run || completion.latest_run || completion);
  const result = asObject(run.result || completion.result || finalization.result);
  const counters = asObject(run.counters || completion.counters);

  const databaseComplete =
    execution.database_complete === true ||
    normalized(execution.phase || execution.status || result.execution_phase) === "database_complete";
  const tenantRootDeleted =
    execution.tenant_root_deleted === true ||
    result.tenant_root_deleted === true ||
    counters.tenant_root_count === 0;

  return (
    <Section
      eyebrow="Protected survivor evidence"
      title="Completion evidence"
      description="Useful lifecycle evidence retained after customer data removal, without exposing destructive execution internals."
      badge="Verified"
      badgeTone="success"
      className="enterprise-admin-lifecycle-completion"
    >
      <div className="enterprise-admin-lifecycle-completion-grid">
        <Card density="compact">
          <span>Completion run ID</span>
          <strong title={run.id || completion.run_id || ""}>
            {shortId(run.id || completion.run_id)}
          </strong>
          <small>Protected lifecycle ledger</small>
        </Card>
        <Card density="compact">
          <span>Run</span>
          <strong>{humanize(run.run_type || completion.run_type || "purge")}</strong>
          <small>{humanize(run.status || completion.status || "succeeded")}</small>
        </Card>
        <Card density="compact">
          <span>Execution</span>
          <strong>{databaseComplete ? "Database complete" : "Completion verified"}</strong>
          <small>{tenantRootDeleted ? "Tenant root deleted" : "Tenant removal verified by finalization"}</small>
        </Card>
        <Card density="compact">
          <span>Retention</span>
          <strong>Governed archive preserved</strong>
          <small>According to applicable retention controls; lifecycle ledger retained.</small>
        </Card>
      </div>
    </Section>
  );
}

export default function LifecycleWorkbench({ request, saving = false, themeMode = "dark" }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [actionKind, setActionKind] = useState(null);
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resolvedTheme = normalized(themeMode) === "light" ? "light" : "dark";

  const loadLifecycle = useCallback(
    async ({ quiet = false } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await request("/enterprise-admin/offboarding");
        setView(data || {});
        return data;
      } catch (err) {
        setError(String(err?.message || err));
        throw err;
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [request]
  );

  useEffect(() => {
    loadLifecycle().catch(() => {});
  }, [loadLifecycle]);

  const items = useMemo(() => asArray(view?.items), [view]);
  const activeRequest = useMemo(() => activeRequestFromView(view), [view]);
  const capabilities = useMemo(() => asObject(view?.capabilities), [view]);
  const actions = useMemo(() => getRequestActions(activeRequest), [activeRequest]);
  const blockers = useMemo(() => getBlockers(activeRequest), [activeRequest]);
  const exportDisposition = useMemo(() => getExportDisposition(activeRequest), [activeRequest]);
  const orchestration = useMemo(() => getOrchestration(activeRequest), [activeRequest]);

  const canRequest = capabilities.can_request === true;
  const canCancel = actions.can_cancel === true;
  const canResolveExport = actions.can_resolve_export === true;
  const canApprove = actions.can_approve === true;

  const currentDisplayStatus = useMemo(() => {
    if (!activeRequest) return "No active request";
    if (blockers.length > 0 && !TERMINAL_REQUEST.has(normalized(activeRequest.status))) return "Blocked";
    if (completionVerified(activeRequest)) return "Completed";
    return humanize(activeRequest.status);
  }, [activeRequest, blockers]);

  const openAction = (kind) => {
    setActionKind(kind);
    setReason("");
    setReference("");
    setActionError("");
    setNotice(null);
  };

  const closeAction = () => {
    if (submitting) return;
    setActionKind(null);
    setActionError("");
  };

  const validateAction = () => {
    const trimmedReason = reason.trim();
    const trimmedReference = reference.trim();
    if (["request", "cancel", "approve", "export_waived"].includes(actionKind) && trimmedReason.length < 3) {
      return "Enter a reason of at least 3 characters.";
    }
    if (actionKind === "export_completed" && !trimmedReference) {
      return "Enter the completion reference for the customer-data export.";
    }
    return "";
  };

  const submitAction = async () => {
    const validation = validateAction();
    if (validation) {
      setActionError(validation);
      return;
    }

    setSubmitting(true);
    setActionError("");

    try {
      const requestId = activeRequest?.id;
      if (actionKind === "request") {
        await request("/customer-lifecycle/offboarding", {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim() }),
        });
      } else if (actionKind === "cancel") {
        await request(`/customer-lifecycle/offboarding/${requestId}/cancel`, {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim() }),
        });
      } else if (actionKind === "export_completed") {
        await request(`/customer-lifecycle/offboarding/${requestId}/export-disposition`, {
          method: "POST",
          body: JSON.stringify({
            status: "completed",
            reference: reference.trim(),
            reason: reason.trim() || undefined,
          }),
        });
      } else if (actionKind === "export_waived") {
        await request(`/customer-lifecycle/offboarding/${requestId}/export-disposition`, {
          method: "POST",
          body: JSON.stringify({ status: "waived", reason: reason.trim() }),
        });
      } else if (actionKind === "approve") {
        await request(`/customer-lifecycle/offboarding/${requestId}/approve`, {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim() }),
        });
      }

      await loadLifecycle({ quiet: true });
      setNotice({
        type: "success",
        message:
          actionKind === "approve"
            ? "Offboarding approved. The governed system workflow remains responsible for execution when all gates permit."
            : "Lifecycle record updated successfully.",
      });
      setActionKind(null);
    } catch (err) {
      setActionError(String(err?.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Section
        eyebrow="Data lifecycle"
        title="Retention & Offboarding"
        description="Loading governed retention, export, and offboarding state."
        className="enterprise-admin-lifecycle-workbench"
      >
        <div className="enterprise-admin-lifecycle-loading" aria-label="Loading lifecycle workbench">
          <Skeleton height="118px" />
          <Skeleton height="220px" />
          <Skeleton height="148px" />
        </div>
      </Section>
    );
  }

  if (error && !view) {
    return (
      <Section
        eyebrow="Data lifecycle"
        title="Retention & Offboarding"
        className="enterprise-admin-lifecycle-workbench"
      >
        <Alert
          tone="danger"
          title="Lifecycle workbench unavailable"
          actions={[{ key: "retry", label: "Retry", onClick: () => loadLifecycle().catch(() => {}) }]}
        >
          {error}
        </Alert>
      </Section>
    );
  }

  const orchestrationPhase =
    orchestration.phase || orchestration.status || activeRequest?.orchestration_phase || "not_started";
  const activeHoldCount =
    activeRequest?.active_hold_count ??
    asObject(activeRequest?.holds || activeRequest?.lifecycle_holds).active_count ??
    blockers.filter((item) => normalized(asObject(item).type || item).includes("hold")).length;
  const approvalBlockers = asArray(actions.approval_blockers);
  const config = actionKind ? ACTION_CONFIG[actionKind] : null;

  return (
    <div className="enterprise-admin-lifecycle-workbench">
      <Section
        eyebrow="Data lifecycle"
        title="Retention & Offboarding"
        description="Govern retention, export disposition, lifecycle approvals, timing, blockers, and protected completion evidence. Offboarding remains a controlled administrative workflow, and destructive execution remains system-side."
        badge={currentDisplayStatus}
        badgeTone={statusTone(currentDisplayStatus)}
        actions={
          <Button
            size="small"
            variant="tertiary"
            onClick={() => loadLifecycle({ quiet: true }).catch(() => {})}
            loading={refreshing}
            loadingLabel="Refreshing…"
          >
            Refresh
          </Button>
        }
      >
        {notice ? (
          <Alert tone={notice.type === "success" ? "success" : "informational"} title="Lifecycle updated">
            {notice.message}
          </Alert>
        ) : null}

        {error ? (
          <Alert tone="warning" title="Latest refresh failed">
            {error}
          </Alert>
        ) : null}

        {!activeRequest ? (
          <EmptyState
            className="enterprise-admin-lifecycle-empty"
            title="No active lifecycle request"
            description="This tenant is operating normally. Retention, export disposition, and offboarding controls are available when needed. Previous lifecycle records remain available below when present."
            actions={
              canRequest
                ? [{ key: "request", label: "Start offboarding request", onClick: () => openAction("request") }]
                : []
            }
          />
        ) : (
          <>
            <div className="enterprise-admin-lifecycle-metrics">
              <LifecycleMetric
                label="Request status"
                value={currentDisplayStatus}
                detail={`Requested ${formatDateTime(activeRequest.requested_at || activeRequest.created_at)}`}
              />
              <LifecycleMetric
                label="Export disposition"
                value={humanize(exportDisposition.status, "Pending")}
                detail={`Deadline ${formatDateTime(activeRequest.export_deadline_at)}`}
              />
              <LifecycleMetric
                label="Purge eligibility"
                value={purgeEligibility(activeRequest) === true ? "Eligible" : "Governed timing"}
                detail={formatDateTime(activeRequest.purge_eligible_at)}
              />
              <LifecycleMetric
                label="System orchestration"
                value={humanize(orchestrationPhase)}
                detail={`Attempt count ${orchestration.attempt_count ?? activeRequest.orchestration_attempt_count ?? 0}`}
              />
            </div>

            <Card className="enterprise-admin-lifecycle-boundary" density="compact">
              <div>
                <p className="enterprise-admin-eyebrow">Authority boundary</p>
                <strong>Administrative approval is not destructive execution.</strong>
                <p>
                  The browser may request, cancel while eligible, resolve export disposition, and approve only when the backend read model advertises those capabilities. Quiescence, purge, and finalization remain system-side.
                </p>
              </div>
              <StatusChip tone="informational">System-side execution</StatusChip>
            </Card>

            <Section
              eyebrow="Governed progression"
              title="Offboarding process"
              description="Read-only lifecycle progression. These steps report state; they are not execution controls."
              density="compact"
              className="enterprise-admin-lifecycle-process-section"
            >
              <LifecycleProcess request={activeRequest} />
            </Section>

            <div className="enterprise-admin-lifecycle-two-column">
              <Section
                eyebrow="Current blockers"
                title={blockers.length ? `${blockers.length} visible blocker${blockers.length === 1 ? "" : "s"}` : "No visible blockers"}
                description="Blockers are supplied by the authoritative lifecycle read model."
                density="compact"
                className="enterprise-admin-lifecycle-blockers"
              >
                {blockers.length ? (
                  <div className="enterprise-admin-lifecycle-blocker-list">
                    {blockers.map((blocker, index) => (
                      <Card key={`${blockerLabel(blocker)}-${index}`} density="compact">
                        <StatusChip tone="warning">Blocked</StatusChip>
                        <strong>{blockerLabel(blocker)}</strong>
                        {asObject(blocker).detail || asObject(blocker).description ? (
                          <small>{asObject(blocker).detail || asObject(blocker).description}</small>
                        ) : null}
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Alert tone="success" title="No visible lifecycle blockers">
                    The read model does not currently report a hold or process blocker for this request.
                  </Alert>
                )}
                <div className="enterprise-admin-lifecycle-hold-summary">
                  <span>Active lifecycle holds</span>
                  <strong>{activeHoldCount}</strong>
                </div>
              </Section>

              <Section
                eyebrow="Administrative actions"
                title="Available governance controls"
                description="Controls are rendered only from backend capability flags for the current administrator and request state."
                density="compact"
                className="enterprise-admin-lifecycle-actions"
              >
                <div className="enterprise-admin-lifecycle-action-grid">
                  {canCancel ? (
                    <Button variant="secondary" onClick={() => openAction("cancel")} disabled={saving}>
                      Cancel request
                    </Button>
                  ) : null}
                  {canResolveExport ? (
                    <>
                      <Button variant="secondary" onClick={() => openAction("export_completed")} disabled={saving}>
                        Mark export completed
                      </Button>
                      <Button variant="tertiary" onClick={() => openAction("export_waived")} disabled={saving}>
                        Waive export
                      </Button>
                    </>
                  ) : null}
                  {canApprove ? (
                    <Button variant="primary" onClick={() => openAction("approve")} disabled={saving}>
                      Approve offboarding
                    </Button>
                  ) : null}
                </div>

                {!canCancel && !canResolveExport && !canApprove ? (
                  <Alert tone="informational" title="No governance action is currently available">
                    The lifecycle read model does not advertise an eligible administrative mutation for this user and request state.
                  </Alert>
                ) : null}

                {approvalBlockers.length ? (
                  <div className="enterprise-admin-lifecycle-capability-note">
                    <span>Approval blockers</span>
                    <strong>{approvalBlockers.map((item) => humanize(item)).join(" · ")}</strong>
                  </div>
                ) : null}
              </Section>
            </div>
          </>
        )}
      </Section>

      <LifecycleCompletionEvidence request={activeRequest || items.find((item) => completionVerified(item))} />

      {items.length ? (
        <Section
          eyebrow="Lifecycle ledger"
          title="Request history"
          description="Governed request history remains visible for administrative traceability."
          density="compact"
          className="enterprise-admin-lifecycle-history-section"
        >
          <LifecycleHistory items={items} />
        </Section>
      ) : null}

      {config ? (
        <Modal
          open
          theme={resolvedTheme}
          eyebrow={config.eyebrow}
          title={config.title}
          description={config.description}
          onClose={closeAction}
          closeOnBackdrop={!submitting}
          closeOnEscape={!submitting}
          size="small"
          className="enterprise-admin-lifecycle-modal"
          actions={[
            {
              key: "cancel",
              label: "Back",
              variant: "tertiary",
              onClick: closeAction,
              disabled: submitting,
            },
            {
              key: "confirm",
              label: config.confirmLabel,
              variant: config.confirmVariant,
              onClick: submitAction,
              loading: submitting,
              loadingLabel: "Saving…",
            },
          ]}
        >
          <div className="enterprise-admin-lifecycle-modal-form">
            {actionKind === "export_completed" ? (
              <Field
                label="Export completion reference"
                required
                help="Use the governed export job, ticket, or customer delivery reference."
              >
                <TextInput
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  placeholder="Export reference"
                  autoComplete="off"
                />
              </Field>
            ) : null}

            <Field
              label={actionKind === "export_completed" ? "Note" : "Reason"}
              required={actionKind !== "export_completed"}
              optionalLabel={actionKind === "export_completed" ? "Optional" : ""}
              help={
                actionKind === "approve"
                  ? "This reason records the human governance decision. Approval does not execute deletion."
                  : "This text becomes part of the governed lifecycle record."
              }
            >
              <TextArea
                rows={5}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={actionKind === "export_completed" ? "Optional completion note" : "Enter governance reason"}
              />
            </Field>

            {actionError ? (
              <Alert tone="danger" title="Unable to save lifecycle action">
                {actionError}
              </Alert>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
